import { useMemo } from 'react'
import { useGetAuthUserList } from '@/service-library/hooks/auth-user'
import { AuthUser } from '@/types/users'

type UseUsersByIdOptions = {
  enabled?: boolean
  /** Only fetch these pd user ids. Omit to fetch the whole account. */
  userIds?: string[]
}

/**
 * Users keyed by pd user id, the id approvers are stored by. A pending user
 * (invited, never logged in) has no pd user id, so it is left out.
 */
export default function useUsersById({
  enabled,
  userIds,
}: UseUsersByIdOptions = {}) {
  // An empty id__in filter is dropped from the request, which would fetch every
  // user in the account. So an empty id list skips the request instead.
  const hasNoIds = userIds?.length === 0

  const { users, isPending, ...query } = useGetAuthUserList({
    enabled: enabled !== false && !hasNoIds,
    filters: {
      fields__only: 'id,name,email',
      // Sorted and deduped so the same set of ids always hits the same cache entry.
      ...(userIds && { id__in: [...new Set(userIds)].sort().join(',') }),
    },
  })

  const usersById = useMemo(
    () =>
      new Map<string, AuthUser>(
        users.flatMap((user) => (user.id ? [[user.id, user]] : [])),
      ),
    [users],
  )

  return {
    usersById,
    isPending: isPending && !hasNoIds,
    ...query,
  }
}
